import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';
import { preferVerticalForBoardBox, shouldRenderBoardVertically } from '../utils/board-orientation';

/**
 * Tracks whether the board should render vertically based on a container's
 * aspect ratio (preferred) or the viewport as a fallback.
 */
export default function useBoardOrientation(
  containerRef?: RefObject<HTMLElement | null>
) {
  const [renderVertically, setRenderVertically] = useState(() =>
    shouldRenderBoardVertically()
  );

  const measure = useCallback(() => {
    const el = containerRef?.current;
    if (el) {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setRenderVertically(preferVerticalForBoardBox(width, height));
        return;
      }
    }
    setRenderVertically(shouldRenderBoardVertically());
  }, [containerRef]);

  useLayoutEffect(() => {
    measure();
    const el = containerRef?.current;
    const ro =
      typeof ResizeObserver !== 'undefined' && el
        ? new ResizeObserver(() => measure())
        : null;
    if (el) ro?.observe(el);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [measure, containerRef]);

  return { renderVertically, remeasure: measure };
}
